"use client";

import { displayLabel } from "@/lib/display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TagMultiSelect } from "@/components/ui/tag-multi-select";
import { OUTCOME_OPTIONS } from "@/lib/utils";
import type { BatchRecord, PlaybookSummary } from "@/lib/api";

const ALL = "__all__";

interface SessionFilterBarProps {
  query: Record<string, string>;
  setQuery: (patch: Record<string, string>) => void;
  availableTags: string[];
  availableIntentDirections?: string[];
  playbooks?: PlaybookSummary[];
  batches?: BatchRecord[];
  showDate?: boolean;
  showPhone?: boolean;
  showCallGroup?: boolean;
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function SessionFilterBar({
  query,
  setQuery,
  availableTags,
  availableIntentDirections,
  playbooks,
  batches,
  showDate = true,
  showPhone = false,
  showCallGroup = false,
}: SessionFilterBarProps) {
  const resetOffset = { offset: "0" };
  const filterTags = query.tags ? query.tags.split(",").filter(Boolean) : [];
  const filterIntentDirs = query.intent_directions
    ? query.intent_directions.split(",").filter(Boolean)
    : [];

  const scopeRow = playbooks || batches;

  return (
    <>
      {scopeRow && (
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          {playbooks && (
            <Select
              value={query.playbook ?? ALL}
              onValueChange={(v) => setQuery({ playbook: v, tags: "", ...resetOffset })}
            >
              <SelectTrigger className="h-8 w-48 text-xs">
                <SelectValue placeholder="全部话术" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>全部话术</SelectItem>
                {playbooks.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {batches && (
            <Select
              value={query.batch ?? ALL}
              onValueChange={(v) => setQuery({ batch: v, ...resetOffset })}
            >
              <SelectTrigger className="h-8 w-48 text-xs">
                <SelectValue placeholder="全部批次" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>全部批次</SelectItem>
                {batches.map((b) => (
                  <SelectItem key={b.batch_id} value={b.batch_id}>
                    {b.batch_id.slice(0, 8)} ({displayLabel(b.status)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-3">
        {availableTags.length > 0 && (
          <FilterField label="标签">
            <TagMultiSelect
              options={availableTags}
              value={filterTags}
              onChange={(next) => setQuery({ tags: next.join(","), ...resetOffset })}
            />
          </FilterField>
        )}

        {availableIntentDirections && availableIntentDirections.length > 0 && (
          <FilterField label="意向方向">
            <TagMultiSelect
              options={availableIntentDirections}
              value={filterIntentDirs}
              onChange={(next) => setQuery({ intent_directions: next.join(","), ...resetOffset })}
            />
          </FilterField>
        )}

        {showPhone && (
          <FilterField label="电话">
            <input
              type="search"
              inputMode="tel"
              placeholder="号码包含…"
              value={query.phone ?? ""}
              onChange={(e) => setQuery({ phone: e.target.value, ...resetOffset })}
              className="h-8 w-40 rounded-md border bg-background px-2 text-xs"
            />
          </FilterField>
        )}

        {showCallGroup && (
          <FilterField label="拨打组 ID">
            <input
              type="search"
              placeholder="精确匹配…"
              value={query.call_group_id ?? ""}
              onChange={(e) => setQuery({ call_group_id: e.target.value, ...resetOffset })}
              className="h-8 w-44 rounded-md border bg-background px-2 text-xs"
            />
          </FilterField>
        )}

        {showDate && (
          <FilterField label="日期">
            <input
              type="date"
              value={query.start ?? ""}
              onChange={(e) => setQuery({ start: e.target.value, ...resetOffset })}
              className="h-8 rounded-md border bg-background px-2 text-xs"
            />
            <span className="text-xs text-muted-foreground">至</span>
            <input
              type="date"
              value={query.end ?? ""}
              onChange={(e) => setQuery({ end: e.target.value, ...resetOffset })}
              className="h-8 rounded-md border bg-background px-2 text-xs"
            />
          </FilterField>
        )}

        <FilterField label="对话结果">
          <Select
            value={query.outcome || ALL}
            onValueChange={(v) => setQuery({ outcome: v, ...resetOffset })}
          >
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>全部</SelectItem>
              {OUTCOME_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{displayLabel(o)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="综合评分 ≥">
          <input
            type="number"
            min={0}
            max={100}
            placeholder="0-100"
            value={query.overall}
            onChange={(e) => setQuery({ overall: e.target.value, ...resetOffset })}
            className="h-8 w-20 rounded-md border bg-background px-2 text-xs"
          />
        </FilterField>

        <FilterField label="目标达成 ≥">
          <input
            type="number"
            min={0}
            max={100}
            placeholder="0-100"
            value={query.goal}
            onChange={(e) => setQuery({ goal: e.target.value, ...resetOffset })}
            className="h-8 w-20 rounded-md border bg-background px-2 text-xs"
          />
        </FilterField>

        <FilterField label="意向评分 ≥">
          <input
            type="number"
            min={0}
            max={100}
            placeholder="0-100"
            value={query.intent}
            onChange={(e) => setQuery({ intent: e.target.value, ...resetOffset })}
            className="h-8 w-20 rounded-md border bg-background px-2 text-xs"
          />
        </FilterField>

        <FilterField label="轮数 ≥">
          <input
            type="number"
            min={0}
            placeholder="≥"
            value={query.turns}
            onChange={(e) => setQuery({ turns: e.target.value, ...resetOffset })}
            className="h-8 w-16 rounded-md border bg-background px-2 text-xs"
          />
        </FilterField>

        <FilterField label="时长 ≥">
          <input
            type="number"
            min={0}
            placeholder="秒"
            value={query.min_duration}
            onChange={(e) => setQuery({ min_duration: e.target.value, ...resetOffset })}
            className="h-8 w-20 rounded-md border bg-background px-2 text-xs"
          />
        </FilterField>

        <FilterField label="审核状态">
          <Select
            value={query.audit_status || "all"}
            onValueChange={(v) => setQuery({ audit_status: v === "all" ? "" : v, ...resetOffset })}
          >
            <SelectTrigger className="h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="unaudited">未审核</SelectItem>
              <SelectItem value="audited">已审核</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="语音信箱">
          <Select
            value={query.amd_filter || "all"}
            onValueChange={(v) => setQuery({ amd_filter: v === "all" ? "" : v, ...resetOffset })}
          >
            <SelectTrigger className="h-8 w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部对话</SelectItem>
              <SelectItem value="only">仅语音信箱</SelectItem>
              <SelectItem value="exclude">排除语音信箱</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
      </div>
    </>
  );
}
